import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem
} from '@utils/storage';

import { TRIAL_MOCK_IDS } from '../constants';

interface TrialModeState {
  isTrialMode: boolean;
  originalStorageValues: Record<string, string> | null;
  originalAccountType: string | null;
}

const readPersistedState = (): TrialModeState => {
  try {
    const parsed = getLocalStorageItem(LOCAL_STORAGE_KEYS.TRIAL_STATE_KEY);
    if (!parsed)
      return {
        isTrialMode: false,
        originalStorageValues: null,
        originalAccountType: null
      };
    return {
      isTrialMode: !!parsed.isTrialMode,
      originalStorageValues: parsed.originalStorageValues || null,
      originalAccountType: parsed.originalAccountType || null
    };
  } catch {
    return {
      isTrialMode: false,
      originalStorageValues: null,
      originalAccountType: null
    };
  }
};

const initialState: TrialModeState = readPersistedState();

const TRIAL_STORAGE_SEED: Record<string, string> = {
  [LOCAL_STORAGE_KEYS.ORGANIZATION_ID]: TRIAL_MOCK_IDS.ORGANIZATION_ID,
  [LOCAL_STORAGE_KEYS.BRANCH_ID]: TRIAL_MOCK_IDS.BRANCH_ID,
  [LOCAL_STORAGE_KEYS.BRANCH_NAME]: TRIAL_MOCK_IDS.BRANCH_NAME
};

export const trialSlice = createSlice({
  name: 'trial',
  initialState,
  reducers: {
    enterTrialMode: (
      state,
      action: PayloadAction<{ accountType?: string } | undefined>
    ) => {
      const saved: Record<string, string> = {};
      Object.keys(TRIAL_STORAGE_SEED).forEach((key) => {
        saved[key] = getLocalStorageItem(key) || '';
      });
      state.originalStorageValues = saved;
      state.originalAccountType = action?.payload?.accountType || null;

      Object.entries(TRIAL_STORAGE_SEED).forEach(([key, value]) => {
        setLocalStorageItem(key, value);
      });

      state.isTrialMode = true;

      setLocalStorageItem(LOCAL_STORAGE_KEYS.TRIAL_STATE_KEY, {
        isTrialMode: true,
        originalStorageValues: saved,
        originalAccountType: state.originalAccountType
      });
    },
    exitTrialMode: (state) => {
      if (state.originalStorageValues) {
        Object.entries(state.originalStorageValues).forEach(([key, value]) =>
          setLocalStorageItem(key, value)
        );
      }
      state.originalStorageValues = null;
      state.originalAccountType = null;
      state.isTrialMode = false;

      removeLocalStorageItem(LOCAL_STORAGE_KEYS.TRIAL_STATE_KEY);
    }
  }
});

export const { enterTrialMode, exitTrialMode } = trialSlice.actions;
export default trialSlice.reducer;
